/**
 * Resolves the FinSuite parent origin that is allowed to receive cross-frame messages from
 * xPT, so `postMessage` can target that exact origin instead of the wildcard `'*'`.
 *
 * Broadcasting with `'*'` sends every reported path (which can include report IDs / query
 * params) to whatever page frames xPT. Restricting to the small, known set of FinSuite
 * origins closes that leak.
 */

// FinSuite website origins allowed to embed xPT. Suffix match covers the alpha/beta/prod and
// personal-stack subdomains under the FinSuite domain families; localhost:4111 is FinSuite dev.
const FINSUITE_ORIGIN_SUFFIXES = ['.finsuite.device.finance.amazon.dev', '.fintech.device.finance.amazon.dev'] as const;
const FINSUITE_ORIGIN_EXACT = new Set<string>([
  'https://finsuite.device.finance.amazon.dev',
  'https://fintech.device.finance.amazon.dev',
  'http://localhost:4111'
]);

/**
 * True when `origin` is a recognized FinSuite website origin. Exact match for the bare prod
 * hosts and dev localhost; otherwise an https host under a FinSuite domain suffix.
 */
export const isFinSuiteOrigin = (origin: string): boolean => {
  if (FINSUITE_ORIGIN_EXACT.has(origin)) return true;
  try {
    const { protocol, hostname } = new URL(origin);
    return protocol === 'https:' && FINSUITE_ORIGIN_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
  } catch {
    return false;
  }
};

/**
 * Resolves the FinSuite parent origin to use as a `postMessage` targetOrigin, or `null` when
 * the embedding parent is not a recognized FinSuite origin (in which case the caller should
 * NOT send — fail closed).
 *
 * Uses `window.location.ancestorOrigins` (the embedding chain). `document.referrer` is not an
 * option because FinSuite mounts the iframe with `referrerPolicy="no-referrer"`, so the
 * referrer is stripped. `ancestorOrigins` is unsupported in Firefox, where this returns `null`
 * and path reporting is skipped — a deliberate fail-closed trade-off that keeps the path from
 * ever leaking to an unverified parent.
 */
export const resolveFinSuiteParentOrigin = (): string | null => {
  const ancestors = window.location.ancestorOrigins;
  const parentOrigin = ancestors && ancestors.length > 0 ? ancestors[0] : null;
  if (!parentOrigin) return null;
  return isFinSuiteOrigin(parentOrigin) ? parentOrigin : null;
};
